import { ReactChild, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  FeatureSubscriptionType,
  PortalPermissions,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  usePortalMembership_GetPortalMembershipQuery,
} from 'queries';

import Footer from 'layouts/common/footer';
import SubscriptionAccessControlModal from 'pages/common/client/subscription-access-control-modal';
import SubscriptionInactiveBanner from 'pages/common/client/subscription-inactive-banner';
import InviteUserModal from 'pages/portal-account/components/manage-portal-users/invite-user-modal';
import { NodeEnvironment } from 'utils/enum';
import { canCollabWithTeamMembers } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import useHasSuperAdmin from 'utils/useHasSuperAdmin';
import { LocalStorageKeys, useLocalStorage } from 'utils/useLocalStorage';

import LeftNavBar, { LEFT_NAV_BAR_WIDTH } from './components/left-nav-bar';
import NavBar from './components/nav-bar';

const useStyles = makeStyles((theme) => ({
  container: { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  root: {
    display: 'flex',
  },
  content: {
    position: 'relative',
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    backgroundColor: '#FFFFFF',
    minHeight: 0,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: LEFT_NAV_BAR_WIDTH,
  },
  footerContainer: {
    marginLeft: 100,
    transition: 'margin 300ms ease',
  },
}));

type Props = {
  children: ReactChild;
  hideFooterOnMobile?: boolean;
  showPortalName?: boolean;
  contentClassName?: string;
};

const estateDashboardRegex =
  /estate\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/estate-dashboard$/;
const estateHomeRegex =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/home$/;
const estateVaultRegex =
  /vault\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const HomeLayout = ({
  children,
  hideFooterOnMobile = false,
  showPortalName = true,
  contentClassName,
}: Props) => {
  const history = useHistory();
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const isSuperAdmin = useHasSuperAdmin();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);
  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const isAssignedTasksPage = history.location.pathname.includes('/assigned-tasks');
  const isEstateAccountingSummaryPage = history.location.pathname.includes(
    '/estate-accounting-summary',
  );
  const isEstateBalanceSheetPage = history.location.pathname.includes('/estate-balance-sheet');
  const isEstateDashboardPage = estateDashboardRegex.test(history.location.pathname);
  const isEstateHomePage = estateHomeRegex.test(history.location.pathname);
  const isEstateSearchResultsPage = history.location.pathname.includes('/estate-results');
  const isEstateVaultPage = estateVaultRegex.test(history.location.pathname);
  const isMyProfilePage = history.location.pathname.includes('/my-profile');
  const isSubscriptionCheckoutPage =
    history.location.pathname.includes('/subscription-pricing-table') ||
    history.location.pathname.includes('/subscription-checkout');
  const isSubscriptionCheckoutSuccessPage = history.location.pathname.includes(
    '/subscription-checkout-success',
  );

  const isAllowedClientPage =
    isEstateAccountingSummaryPage ||
    isEstateBalanceSheetPage ||
    isEstateDashboardPage ||
    isEstateHomePage ||
    isEstateSearchResultsPage ||
    isEstateVaultPage ||
    isMyProfilePage;

  const isLeftNavClosed =
    isAssignedTasksPage ||
    isEstateBalanceSheetPage ||
    isEstateDashboardPage ||
    isSubscriptionCheckoutSuccessPage;

  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [isLeftNavBarOpen, setIsLeftNavBarOpen] = useState(!!portalId && !isLeftNavClosed);
  const [isInviteTeamMemberBannerVisible, setIsInviteTeamMemberBannerVisible] = useLocalStorage(
    LocalStorageKeys.InviteTeamMemberBanner,
    true,
  );

  const { data: portalMembershipData } = usePortalMembership_GetPortalMembershipQuery({
    skip: !portalId,
    variables: { portalId: portalId },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal membership: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: featureSubscriptionTypeData } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      skip: !portalId,
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  useEffect(() => {
    if (
      portalMembershipData &&
      featureSubscriptionTypeData &&
      portalMembershipData.data.isGettingStarted &&
      featureSubscriptionTypeData.data === FeatureSubscriptionType.None
    ) {
      // If new account and no subscription, send to subscription pricing table page
      //  They can still access their profile page however.
      if (
        !isSubscriptionCheckoutPage &&
        !isMyProfilePage &&
        process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD
      ) {
        history.replace(`/home/${portalId}/subscription-pricing-table`);
      }
    } else if (hasPortalPermissionClient) {
      // If shared client user, send to home page if they're aren't on an allowed page
      if (!isAllowedClientPage) {
        history.replace(`/home/${portalId}/home`);
      }
    }
  }, [
    portalId,
    portalMembershipData,
    featureSubscriptionTypeData,
    hasPortalPermissionClient,
    history,
    isSubscriptionCheckoutPage,
    isMyProfilePage,
    isAllowedClientPage,
  ]);

  const showMainContent =
    isSuperAdmin ||
    hasPortalPermissionAdmin ||
    hasPortalPermissionUser ||
    (hasPortalPermissionClient && isAllowedClientPage);

  const onAddTeamMemberClicked = () => {
    if (
      !featureSubscriptionTypeData?.data ||
      !canCollabWithTeamMembers(featureSubscriptionTypeData?.data)
    ) {
      showToast({
        message: 'Please upgrade your account to collaborate with team members.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setShowInviteUserModal(true);
  };

  const showInviteTeamMemberBanner = isInviteTeamMemberBannerVisible && hasPortalPermissionAdmin;

  return (
    <>
      {!!portalId && featureSubscriptionTypeData?.data !== FeatureSubscriptionType.None && (
        <SubscriptionAccessControlModal />
      )}
      <div className={classes.container}>
        <NavBar
          showPortalName={showPortalName}
          setIsLeftNavBarOpen={setIsLeftNavBarOpen}
          isLeftNavBarOpen={isLeftNavBarOpen}
          onAddTeamMemberClicked={onAddTeamMemberClicked}
          isInviteTeamMemberBannerVisible={showInviteTeamMemberBanner}
          setIsInviteTeamMemberBannerVisible={setIsInviteTeamMemberBannerVisible}
        />
        <CssBaseline />
        {portalId && (
          <LeftNavBar
            isOpen={isLeftNavBarOpen}
            isInviteTeamMemberBannerVisible={showInviteTeamMemberBanner}
            featureSubscriptionType={
              featureSubscriptionTypeData?.data || FeatureSubscriptionType.None
            }
          />
        )}
        {showMainContent && (
          <main
            className={clsx(classes.content, contentClassName, {
              [classes.contentShift]: isLeftNavBarOpen,
            })}
          >
            {!!portalId &&
              portalMembershipData?.data &&
              featureSubscriptionTypeData?.data &&
              !portalMembershipData.data.isGettingStarted &&
              featureSubscriptionTypeData.data === FeatureSubscriptionType.None && (
                <SubscriptionInactiveBanner />
              )}
            {children}
          </main>
        )}
        {hasPortalPermissionAdmin && (
          <InviteUserModal showModal={showInviteUserModal} setShowModal={setShowInviteUserModal} />
        )}
        <Box className={isLeftNavBarOpen ? classes.footerContainer : ''}>
          <Footer hideOnMobile={hideFooterOnMobile} />
        </Box>
      </div>
    </>
  );
};

export default HomeLayout;
